// frontend/src/components/landing/LandingPage.jsx
import React, { useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
    Shield,
    Monitor,
    Wifi,
    Network,
    BarChart3,
    Lock,
    Eye,
    Usb,
    FileSearch,
    Globe,
    Activity,
    ArrowRight,
    ArrowLeft,
    Mail,
    MapPin,
    Phone,
    Brain,
    Check,
    Minus,
    Users,
    Menu,
    X,
    Wifi as WifiIcon,
    AlertTriangle,
    TrendingUp,
    Clock,
    Bot,
    Terminal,
    Layers,
    User,
    Building2,
    Briefcase,
    Target,
    Zap,
    CalendarCheck,
    CreditCard,
    ShieldCheck,
    Headphones,
    Trophy,
    CalendarClock,
    Sparkles,
} from "lucide-react";
import ThemeToggle from "../ThemeToggle.jsx";
import logo from "../../assets/gca.png";
import pricingTiers from "./pricing-tiers.json";
import { getToken } from "../../utils/authService";
import { apiPublicGet, apiPublicPost } from "../../utils/api";
import { SUPPORT_EMAIL } from "../../utils/supportContacts";
import { applyPlanDisplayCopy, getPlanPublicId, getStoredPlanId } from "../../utils/pricingDisplay.js";
import { toast } from "../../utils/toast";
import { trackEvent } from "../../utils/metaPixel";
import "./landing.css";

/* --- Animated Section Wrapper --- */
function FadeInSection({ children, delay = 0, className = "" }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-80px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay, ease: "easeOut" }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/* --- Feature Card --- */
function FeatureCard({ icon: Icon, title, description, delay }) {
    return (
        <FadeInSection delay={delay}>
            <div className="landing-feature-card">
                <div className="landing-feature-icon">
                    <Icon size={28} strokeWidth={1.5} />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
            </div>
        </FadeInSection>
    );
}

/* --- Branch Locations --- */
const branchLocations = [
    {
        countryCode: "US",
        title: "HQ",
        subtitle: "Charlotte, North Carolina · United States",
        addressLine1: "Tully House Ct, 15451",
        addressLine2: "",
        city: "Charlotte",
        state: "North Carolina",
        zip: "28277",
        country: "United States",
    },
    {
        countryCode: "US",
        title: "R&D | Sales",
        subtitle: "Edison, New Jersey · United States",
        addressLine1: "190 Lincoln Hwy, 304",
        addressLine2: "",
        city: "Edison",
        state: "New Jersey",
        zip: "08817",
        country: "United States",
    },
    {
        countryCode: "IN",
        title: "Engineering | Sales",
        subtitle: "Chennai, Tamil Nadu · India",
        addressLine1: "NO 5, 5th Street, Dr Radha Krishnan Salai",
        addressLine2: "Sullivan Garden, Mylapore",
        city: "Chennai",
        state: "Tamil Nadu",
        zip: "600004",
        country: "India",
    },
];

/* ===========================================================
   FEATURES DATA
   =========================================================== */
const features = [
    {
        icon: Monitor,
        title: "Real-Time Device Monitoring",
        description:
            "Track every endpoint across your fleet with live status, performance metrics, and CPU/RAM/disk insights.",
    },
    {
        icon: Network,
        title: "Network Visualizer",
        description:
            "Interactive force-graph topology mapping of your entire network. See every node, connection, and anomaly.",
    },
    {
        icon: Shield,
        title: "Vulnerability Scanning",
        description:
            "Automated CVE detection and risk scoring across all connected devices with one-click remediation guidance.",
    },
    {
        icon: Usb,
        title: "USB Device Control",
        description:
            "Monitor, whitelist, or block USB storage devices organization-wide with real-time policy enforcement.",
    },
    {
        icon: FileSearch,
        title: "File Monitor & DLP",
        description:
            "Watch for unauthorized file changes, exfiltration attempts, and enforce data loss prevention policies.",
    },
    {
        icon: Globe,
        title: "URL & Browser Tracker",
        description:
            "Track foreground browser URLs and domain activity per user with category-level analytics.",
    },
    {
        icon: BarChart3,
        title: "Executive Analytics",
        description:
            "Role-based dashboards (CEO, CFO, Manager) with productivity metrics, cost analysis, and workforce insights.",
    },
    {
        icon: Activity,
        title: "Performance Intelligence",
        description:
            "Active vs idle time tracking with foreground app analysis. Know exactly where productivity hours go.",
    },
    {
        icon: Lock,
        title: "Multi-Tenant RBAC",
        description:
            "Enterprise-grade role-based access with per-view permissions. Isolate teams, clients, and subsidiaries.",
    },
];

/* ===========================================================
   TRUSTED-BY LOGO STRIP
   =========================================================== */
const trustedByLogos = [
    "SMEC Automation",
    "Teknoware",
    "VVDN Technologies",
    "Polycab",
    "Galaxy Sivtek",
    "Andritz",
];

/* ===========================================================
   HERO VALUE STRIP DATA
   =========================================================== */
const heroValueStripItems = [
    {
        icon: Zap,
        title: "Live in 24 hours.",
        description: "Rapid deployment with minimal disruption.",
    },
    {
        icon: ShieldCheck,
        title: "Secure by design.",
        description: "Meets global data-privacy standards from day one.",
    },
    {
        icon: Layers,
        title: "Fits your stack.",
        description: "Seamless integration with existing systems.",
    },
    {
        icon: Globe,
        title: "Runs anywhere.",
        description: "Optimized for diverse, complex environments.",
    },
    {
        icon: BarChart3,
        title: "Insight that drives decisions.",
        description: "200+ customizable reports.",
    },
    {
        icon: Sparkles,
        title: "Effortless to use.",
        description: "An intuitive interface, adopted without training.",
    },
];

/* ===========================================================
   HERO MAIN CAROUSEL DATA — slide 1 is the original full pitch
   (headline, checklist, trust badges); the rest are stat cards.
   One rotation, not two separate carousels.
   =========================================================== */
const heroMainSlides = [
    {
        icon: Layers,
        eyebrow: "For Business Leaders - CEOs, CFOs, Managers, HRs & Employees",
        headline: "Get Complete Visibility.",
        headlineAccent: "Drive Better Performance.",
        sub: "VisuN AI gives you real-time visibility into your teams, projects and company devices — so you can improve productivity, ensure accountability and achieve business goals faster. One dashboard replaces a dozen disconnected tools, spreadsheets and manual check-ins.",
        checklist: [
            "Know how work gets done",
            "Identify bottlenecks and productivity gaps",
            "Make data-driven decisions with confidence",
        ],
    },
    {
        icon: Monitor,
        value: "500+",
        valueLabel: "Agents Installed",
        eyebrow: "Device Monitoring",
        headline: "See Every Screen, Live",
        headlineAccent: "Across Your Whole Team.",
        sub: "Real-time screen capture and productivity tracking on every Windows device you manage, from a single unified dashboard — 500+ agents installed and counting.",
        checklist: [
            "Live screen viewing on demand",
            "Automatic online / away / offline status",
            "Task manager and performance view per device",
        ],
    },
    {
        icon: FileSearch,
        value: "12M+",
        valueLabel: "Files Monitored",
        eyebrow: "Activity Monitoring",
        headline: "Nothing Slips Through,",
        headlineAccent: "Not a File or a Click.",
        sub: "Every file rename, upload and transfer logged automatically — alongside USB device control and full URL activity tracking, all searchable from one timeline. 12M+ files monitored.",
        checklist: [
            "USB device connect / block alerts",
            "Complete browsing and URL history",
            "Google Safe Browsing URL threat detection",
        ],
    },
    {
        icon: Building2,
        value: "150+",
        valueLabel: "Enterprise Clients",
        eyebrow: "Enterprise Ready",
        headline: "Built for Every Business,",
        headlineAccent: "From Startups to Enterprises.",
        sub: "From manufacturing floors to remote-first teams, organizations trust VisuN AI to run monitoring across their whole workforce.",
        checklist: [
            "Multi-tenant, role-based access",
            "Enterprise-grade data isolation",
        ],
    },
    {
        icon: Activity,
        value: "99.99%",
        valueLabel: "Uptime Reliability",
        eyebrow: "Reliability",
        headline: "Always On,",
        headlineAccent: "Always Reliable.",
        sub: "Live status pushed straight to your dashboard over real-time connections — no polling, no stale data. 99.99% uptime reliability, no matter the scale.",
        checklist: [
            "Real-time updates, not batch refresh",
            "Built for always-on operations",
        ],
    },
    {
        icon: Eye,
        value: "24/7",
        valueLabel: "Real-Time Monitoring",
        eyebrow: "AI-Powered Insight",
        headline: "Data That Talks Back —",
        headlineAccent: "Ask It, Get Answers.",
        sub: "Screen activity, app usage and productivity analytics streamed live — plus an AI assistant that answers questions about your own data, in plain English, 24/7.",
        checklist: [
            "AI chat over your live monitoring data",
            "Productivity analytics dashboards",
            "Understands plain-English questions, no query language",
        ],
    },
];

// Extracted so the exact same markup can be rendered both visibly (inside
// the animated slide) and invisibly (in the measurement stack that sizes
// the carousel — see HeroMainCarousel below).
function HeroSlideContent({ slide }) {
    return (
        <>
            <div className="landing-hero-slide-header">
                <div className="landing-hero-stats-icon">
                    <slide.icon size={17} strokeWidth={1.75} />
                </div>
                <span className="landing-hero-v2-eyebrow">{slide.eyebrow}</span>
            </div>
            <h1>
                {slide.headline}
                <br />
                <span className="landing-hero-v2-accent">{slide.headlineAccent}</span>
            </h1>
            <p className="landing-hero-v2-sub">{slide.sub}</p>
            <ul className="landing-hero-v2-checklist">
                {slide.checklist.map((item) => (
                    <li key={item}><Check size={16} /> {item}</li>
                ))}
            </ul>
            {slide.value && (
                <div className="landing-hero-slide-bottom-value">
                    <span className="landing-hero-slide-bottom-value-number">{slide.value}</span>
                    <span className="landing-hero-slide-bottom-value-label">{slide.valueLabel}</span>
                </div>
            )}
        </>
    );
}

function HeroMainCarousel() {
    const [index, setIndex] = React.useState(0);
    const [direction, setDirection] = React.useState(1); // 1 = advancing left-to-right, -1 = reversing
    const timerRef = React.useRef(null);

    const restartAutoplay = React.useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setDirection(1);
            setIndex((i) => (i + 1) % heroMainSlides.length);
        }, 4500);
    }, []);

    React.useEffect(() => {
        restartAutoplay();
        return () => clearInterval(timerRef.current);
    }, [restartAutoplay]);

    const goTo = (i) => {
        setDirection(i > index || (index === heroMainSlides.length - 1 && i === 0) ? 1 : -1);
        setIndex(i);
        restartAutoplay();
    };
    const goNext = () => goTo((index + 1) % heroMainSlides.length);
    const goPrev = () => goTo((index - 1 + heroMainSlides.length) % heroMainSlides.length);

    const slide = heroMainSlides[index];

    // Measures every slide's real rendered height (off-screen, same width as
    // the visible carousel) and sizes the container to fit the tallest one —
    // so the CTA buttons below the carousel in normal page flow sit at a
    // truly fixed position instead of one guessed at from a hardcoded px
    // value, which kept being too short for whichever slide had the most
    // content.
    const measureRefs = React.useRef([]);
    const [carouselHeight, setCarouselHeight] = React.useState(null);

    React.useEffect(() => {
        const measure = () => {
            const heights = measureRefs.current.map((el) => el?.offsetHeight || 0);
            const max = Math.max(...heights, 0);
            if (max > 0) setCarouselHeight(max);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    return (
        <div
            className="landing-hero-main-carousel"
            style={carouselHeight ? { height: `${carouselHeight + 36}px` } : undefined}
        >
            <div className="landing-hero-main-measure" aria-hidden="true">
                {heroMainSlides.map((s, i) => (
                    <div key={i} ref={(el) => { measureRefs.current[i] = el; }}>
                        <HeroSlideContent slide={s} />
                    </div>
                ))}
            </div>
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={index}
                    custom={direction}
                    initial={{ opacity: 0, x: direction > 0 ? 36 : -36 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: direction > 0 ? -36 : 36 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                >
                    <HeroSlideContent slide={slide} />
                </motion.div>
            </AnimatePresence>
            <div className="landing-hero-main-controls">
                <div className="landing-hero-main-dots">
                    {heroMainSlides.map((s, i) => (
                        <button
                            key={i}
                            className={`landing-hero-main-dot ${i === index ? "active" : ""}`}
                            onClick={() => goTo(i)}
                            aria-label={`Show ${s.eyebrow}`}
                        />
                    ))}
                </div>
                <div className="landing-hero-main-arrows">
                    <motion.button
                        type="button"
                        className="landing-hero-main-arrow landing-hero-main-arrow--prev"
                        onClick={goPrev}
                        aria-label="Previous slide"
                        whileHover={{ x: -3 }}
                        whileTap={{ scale: 0.85 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                        <ArrowLeft size={18} strokeWidth={1.75} />
                    </motion.button>
                    <motion.button
                        type="button"
                        className="landing-hero-main-arrow landing-hero-main-arrow--next"
                        onClick={goNext}
                        aria-label="Next slide"
                        whileHover={{ x: 3 }}
                        whileTap={{ scale: 0.85 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                        <ArrowRight size={18} strokeWidth={1.75} />
                    </motion.button>
                </div>
            </div>
        </div>
    );
}

function normalizeTextList(rawList) {
    if (!Array.isArray(rawList)) return [];
    return rawList
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
}

function formatLandingPrice(priceInPaise, contactSales) {
    if (contactSales) return "Contact Sales";
    const numeric = Number(priceInPaise);
    if (!Number.isFinite(numeric) || numeric <= 0) return "Free";
    const units = numeric / 100;
    if (Number.isInteger(units)) return String(units);
    return units.toFixed(2);
}

function mergeLandingTiersWithServer(serverPlans = []) {
    const byId = new Map(
        (Array.isArray(serverPlans) ? serverPlans : [])
            .filter((plan) => plan && typeof plan.id === "string")
            .map((plan) => [getStoredPlanId(plan.id), plan])
    );

    return pricingTiers.map((tier) => {
        const storedTierId = getStoredPlanId(tier.id);
        const hasServerPlan = byId.has(storedTierId);
        const serverPlan = byId.get(storedTierId) || {};
        const contactSales = typeof serverPlan.contactSales === "boolean"
            ? serverPlan.contactSales
            : !!tier.contactSales;
        const fallbackTierPriceInPaise = Number(tier.priceInPaise || 0);
        const rawServerPriceInPaise = Number(serverPlan.priceInPaise);
        const priceInPaise = Number.isFinite(rawServerPriceInPaise)
            ? rawServerPriceInPaise
            : fallbackTierPriceInPaise;
        const requiresPayment = typeof serverPlan.requiresPayment === "boolean"
            ? serverPlan.requiresPayment
            : !!tier.requiresPayment;
        const hasPricingError = getPlanPublicId(tier.id) !== "free"
            && !contactSales
            && hasServerPlan
            && (!requiresPayment || !Number.isFinite(rawServerPriceInPaise) || rawServerPriceInPaise <= 0);

        return applyPlanDisplayCopy({
            ...tier,
            name: String(serverPlan.name || tier.name || ""),
            description: String(serverPlan.description || tier.description || ""),
            billing: String(serverPlan.billing || tier.billing || "").trim(),
            price: hasPricingError
                ? (tier.id === "enterprise" ? "Custom pricing" : "Pricing unavailable")
                : formatLandingPrice(priceInPaise, contactSales),
            features: Array.isArray(serverPlan.features)
                ? normalizeTextList(serverPlan.features)
                : normalizeTextList(tier.features),
            cta: String(serverPlan.cta || tier.cta || ""),
            featured: typeof serverPlan.featured === "boolean" ? serverPlan.featured : !!tier.featured,
            contactSales,
        });
    });
}

/* ===========================================================
   HOW IT WORKS DATA
   =========================================================== */
const howItWorksSteps = [
    {
        step: "01",
        title: "Deploy the Agent",
        description:
            "Download and install the lightweight VisuN AI agent on your Windows endpoints. Setup takes under 2 minutes per machine — no reboots required.",
        icon: Terminal,
    },
    {
        step: "02",
        title: "Stream Real-Time Data",
        description:
            "Agents instantly begin streaming CPU, RAM, disk, app activity, USB events, file changes, and browser URLs to your secure cloud dashboard.",
        icon: WifiIcon,
    },
    {
        step: "03",
        title: "Act on AI-Powered Insights",
        description:
            "VisuN AI surfaces anomalies, productivity trends, and security threats automatically — with role-based dashboards for your CEO, CFO, and security team.",
        icon: Brain,
    },
];

/* ===========================================================
   PRODUCT PREVIEW TABS
   =========================================================== */
const previewTabs = [
    {
        id: "devices",
        label: "Device Fleet",
        icon: Monitor,
        description: "Real-time endpoint status across your entire fleet",
    },
    {
        id: "analytics",
        label: "CEO Dashboard",
        icon: BarChart3,
        description: "Executive productivity and workforce analytics",
    },
    {
        id: "security",
        label: "Security Pulse",
        icon: Shield,
        description: "Threat detection, USB events, and URL risk scoring",
    },
    {
        id: "visunai",
        label: "VisuN AI",
        icon: Bot,
        description: "AI-powered insights and natural-language queries",
    },
];

/* --- Static mock data for each preview tab --- */
const mockDevices = [
    { id: "WIN-SALES-01", hostname: "LAPTOP-MKUMAR", user: "Muthu Kumar", status: "online", cpu: 34, ram: 58, os: "Windows 11", lastSeen: "Live" },
    { id: "WIN-DEV-03", hostname: "DESKTOP-RPRIYA", user: "Priya R.", status: "online", cpu: 71, ram: 82, os: "Windows 10", lastSeen: "Live" },
    { id: "WIN-HR-07", hostname: "LAPTOP-ASENTHIL", user: "Senthil A.", status: "idle", cpu: 4, ram: 31, os: "Windows 11", lastSeen: "4m ago" },
    { id: "WIN-FIN-02", hostname: "DESKTOP-SVIJAY", user: "Vijay S.", status: "offline", cpu: 0, ram: 0, os: "Windows 10", lastSeen: "2h ago" },
    { id: "WIN-OPS-11", hostname: "LAPTOP-NKARTHIK", user: "Karthik N.", status: "online", cpu: 22, ram: 44, os: "Windows 11", lastSeen: "Live" },
];

const mockAnalytics = {
    productivity: 78,
    active: 31,
    total: 38,
    focusScore: 82,
    topApps: [
        { name: "Microsoft Teams", pct: 28, color: "#5558af" },
        { name: "Google Chrome", pct: 22, color: "#00b4d8" },
        { name: "VS Code", pct: 18, color: "#007acc" },
        { name: "Excel", pct: 14, color: "#217346" },
        { name: "Others", pct: 18, color: "#4a5568" },
    ],
    attendance: [
        { day: "Mon", pct: 92 },
        { day: "Tue", pct: 88 },
        { day: "Wed", pct: 95 },
        { day: "Thu", pct: 84 },
        { day: "Fri", pct: 79 },
    ],
};

const mockSecurity = [
    { type: "Unsafe Web Activity", agent: "WIN-SALES-01", severity: "Critical", time: "2m ago", detail: "torrent-site.net" },
    { type: "Unauthorized USB", agent: "WIN-DEV-03", severity: "High", time: "14m ago", detail: "Kingston USB 32GB" },
    { type: "File Manipulation", agent: "WIN-HR-07", severity: "Medium", time: "1h ago", detail: "/HR/salaries.xlsx moved" },
    { type: "Restricted App", agent: "WIN-OPS-11", severity: "Low", time: "3h ago", detail: "Steam.exe launched" },
];

const mockAiMessages = [
    { role: "user", text: "Which employees had the lowest productivity this week?" },
    { role: "ai", text: "Based on active time analysis for the week of May 26–30, the 3 employees with the lowest productivity scores are:\n\n1. Vijay S. (WIN-FIN-02) — 41% active time, offline for 2 days\n2. Senthil A. (WIN-HR-07) — 53% active time, frequent idle periods\n3. Karthik N. (WIN-OPS-11) — 61% active time\n\nAll three show below-team-average focus scores. Would you like a detailed breakdown?" },
    { role: "user", text: "Show me USB events from the last 24 hours" },
    { role: "ai", text: "Found 3 USB events in the last 24 hours:\n\n- Kingston 32GB inserted on WIN-DEV-03 (14m ago) — BLOCKED by policy\n- SanDisk 16GB removed from WIN-SALES-01 (4h ago)\n- WD Passport 1TB inserted on WIN-FIN-02 (22h ago) — ALLOWED (whitelisted)" },
];

/* --- Severity badge helper --- */
function SevBadge({ severity }) {
    const map = {
        Critical: "lp-sev-critical",
        High: "lp-sev-high",
        Medium: "lp-sev-medium",
        Low: "lp-sev-low",
    };
    return <span className={`lp-sev-badge ${map[severity] || "lp-sev-low"}`}>{severity}</span>;
}

/* --- Status dot helper --- */
function StatusDot({ status }) {
    const map = { online: "lp-dot-online", idle: "lp-dot-idle", offline: "lp-dot-offline" };
    return <span className={`lp-status-dot ${map[status] || "lp-dot-offline"}`} />;
}

/* --- Mini bar component --- */
function MiniBar({ value, color = "var(--lp-accent)" }) {
    return (
        <div className="lp-mini-bar-track">
            <div className="lp-mini-bar-fill" style={{ width: `${value}%`, background: color }} />
        </div>
    );
}

/* --- Device Fleet Preview --- */
function DevicesPreview() {
    return (
        <div className="lp-preview-panel">
            <div className="lp-preview-toolbar">
                <span className="lp-preview-toolbar-title"><Monitor size={14} /> Device Fleet</span>
                <span className="lp-preview-toolbar-badge lp-badge-green">5 agents</span>
            </div>
            <div className="lp-preview-table">
                <div className="lp-preview-thead">
                    <span>Hostname</span>
                    <span>User</span>
                    <span>Status</span>
                    <span>CPU</span>
                    <span>RAM</span>
                    <span>Last Seen</span>
                </div>
                {mockDevices.map((d) => (
                    <div key={d.id} className="lp-preview-trow">
                        <span className="lp-preview-monospace">{d.hostname}</span>
                        <span>{d.user}</span>
                        <span className="lp-preview-status">
                            <StatusDot status={d.status} />
                            {d.status}
                        </span>
                        <span>
                            {d.status !== "offline" ? (
                                <div className="lp-preview-metric">
                                    <MiniBar value={d.cpu} color={d.cpu > 70 ? "#f59e0b" : "var(--lp-accent)"} />
                                    <small>{d.cpu}%</small>
                                </div>
                            ) : <span className="lp-muted">—</span>}
                        </span>
                        <span>
                            {d.status !== "offline" ? (
                                <div className="lp-preview-metric">
                                    <MiniBar value={d.ram} color={d.ram > 80 ? "#ef4444" : "var(--lp-accent)"} />
                                    <small>{d.ram}%</small>
                                </div>
                            ) : <span className="lp-muted">—</span>}
                        </span>
                        <span className={d.lastSeen === "Live" ? "lp-live-badge" : "lp-muted"}>{d.lastSeen}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* --- CEO Analytics Preview --- */
function AnalyticsPreview() {
    return (
        <div className="lp-preview-panel">
            <div className="lp-preview-toolbar">
                <span className="lp-preview-toolbar-title"><BarChart3 size={14} /> CEO Dashboard</span>
                <span className="lp-preview-toolbar-badge">Today</span>
            </div>
            <div className="lp-analytics-grid">
                <div className="lp-kpi-card">
                    <span className="lp-kpi-label">Productivity Score</span>
                    <span className="lp-kpi-value lp-kpi-accent">{mockAnalytics.productivity}%</span>
                    <span className="lp-kpi-sub"><TrendingUp size={11} /> +4% vs last week</span>
                </div>
                <div className="lp-kpi-card">
                    <span className="lp-kpi-label">Active Employees</span>
                    <span className="lp-kpi-value">{mockAnalytics.active}<small>/{mockAnalytics.total}</small></span>
                    <span className="lp-kpi-sub"><Users size={11} /> {Math.round(mockAnalytics.active / mockAnalytics.total * 100)}% present</span>
                </div>
                <div className="lp-kpi-card">
                    <span className="lp-kpi-label">Focus Score</span>
                    <span className="lp-kpi-value lp-kpi-accent">{mockAnalytics.focusScore}</span>
                    <span className="lp-kpi-sub"><Activity size={11} /> AI-computed</span>
                </div>
            </div>
            <div className="lp-analytics-bottom">
                <div className="lp-chart-block">
                    <div className="lp-chart-title">Top Applications</div>
                    {mockAnalytics.topApps.map((app) => (
                        <div key={app.name} className="lp-app-row">
                            <span className="lp-app-name">{app.name}</span>
                            <div className="lp-mini-bar-track lp-app-bar">
                                <div className="lp-mini-bar-fill" style={{ width: `${app.pct}%`, background: app.color }} />
                            </div>
                            <span className="lp-app-pct">{app.pct}%</span>
                        </div>
                    ))}
                </div>
                <div className="lp-chart-block">
                    <div className="lp-chart-title">Weekly Attendance</div>
                    <div className="lp-bar-chart">
                        {mockAnalytics.attendance.map((day) => (
                            <div key={day.day} className="lp-bar-col">
                                <div className="lp-bar-fill-wrap">
                                    <div className="lp-bar-fill" style={{ height: `${day.pct}%` }} />
                                </div>
                                <span className="lp-bar-label">{day.day}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* --- Security Pulse Preview --- */
function SecurityPreview() {
    return (
        <div className="lp-preview-panel">
            <div className="lp-preview-toolbar">
                <span className="lp-preview-toolbar-title"><Shield size={14} /> Security Pulse</span>
                <span className="lp-preview-toolbar-badge lp-badge-red">4 alerts</span>
            </div>
            <div className="lp-security-list">
                {mockSecurity.map((alert, i) => (
                    <div key={i} className="lp-security-row">
                        <AlertTriangle size={14} className={`lp-alert-icon lp-alert-${alert.severity.toLowerCase()}`} />
                        <div className="lp-security-body">
                            <div className="lp-security-top">
                                <span className="lp-security-type">{alert.type}</span>
                                <SevBadge severity={alert.severity} />
                            </div>
                            <div className="lp-security-detail">{alert.detail}</div>
                            <div className="lp-security-meta">
                                <span>{alert.agent}</span>
                                <span className="lp-security-time">{alert.time}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* --- VisuN AI Preview --- */
function VisunAiPreview() {
    return (
        <div className="lp-preview-panel lp-ai-panel">
            <div className="lp-preview-toolbar">
                <span className="lp-preview-toolbar-title"><Bot size={14} /> VisuN AI</span>
                <span className="lp-preview-toolbar-badge lp-badge-purple">Deepseek + Gemini</span>
            </div>
            <div className="lp-ai-chat">
                {mockAiMessages.map((msg, i) => (
                    <div key={i} className={`lp-ai-msg lp-ai-msg-${msg.role}`}>
                        <div className="lp-ai-msg-bubble">
                            {msg.text.split("\n").map((line, j) => (
                                <span key={j}>{line}{j < msg.text.split("\n").length - 1 && <br />}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className="lp-ai-input-mock">
                <span>Ask VisuN AI anything about your fleet...</span>
                <span className="lp-ai-input-icon"><Bot size={14} /></span>
            </div>
        </div>
    );
}

/* --- Product Preview Section --- */
function ProductPreview() {
    const [activeTab, setActiveTab] = React.useState("devices");

    const renderPanel = () => {
        switch (activeTab) {
            case "devices": return <DevicesPreview />;
            case "analytics": return <AnalyticsPreview />;
            case "security": return <SecurityPreview />;
            case "visunai": return <VisunAiPreview />;
            default: return <DevicesPreview />;
        }
    };

    return (
        <section id="product" className="landing-product-preview">
            <FadeInSection>
                <div className="landing-section-header">
                    <span className="landing-section-tag">Product Preview</span>
                    <h2>
                        See the real platform,{" "}
                        <span className="text-accent">not a mockup</span>
                    </h2>
                    <p>
                        Every screen below mirrors actual Visun AI modules — with real
                        navigation, real data structures, and real workflows your team
                        will use every day.
                    </p>
                </div>
            </FadeInSection>

            <FadeInSection delay={0.1}>
                {/* Browser chrome wrapper */}
                <div className="lp-product-chrome">
                    <div className="lp-product-chrome-bar">
                        <div className="lp-chrome-dots">
                            <span /><span /><span />
                        </div>
                        <div className="lp-chrome-tabs">
                            {previewTabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        className={`lp-chrome-tab ${activeTab === tab.id ? "is-active" : ""}`}
                                        onClick={() => setActiveTab(tab.id)}
                                    >
                                        <Icon size={13} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                        <span className="lp-chrome-url">app.visunai.com — {previewTabs.find(t => t.id === activeTab)?.description}</span>
                    </div>

                    {/* Sidebar + content */}
                    <div className="lp-product-body">
                        {/* Mini sidebar */}
                        <nav className="lp-mini-sidebar">
                            <div className="lp-mini-sidebar-logo">
                                <Shield size={18} />
                                <span>VisuN AI</span>
                            </div>
                            <ul className="lp-mini-nav">
                                {[
                                    { id: "devices", icon: Monitor, label: "Devices" },
                                    { id: "analytics", icon: BarChart3, label: "Analytics" },
                                    { id: "security", icon: Shield, label: "Security" },
                                    { id: "visunai", icon: Bot, label: "VisuN AI" },
                                    { id: "network", icon: Network, label: "Visualizer" },
                                    { id: "usb", icon: Usb, label: "USB Control" },
                                    { id: "files", icon: FileSearch, label: "File Monitor" },
                                    { id: "billing", icon: Layers, label: "Billing" },
                                ].map((item) => {
                                    const Icon = item.icon;
                                    const isInteractive = ["devices", "analytics", "security", "visunai"].includes(item.id);
                                    return (
                                        <li
                                            key={item.id}
                                            className={`lp-mini-nav-item ${activeTab === item.id ? "is-active" : ""} ${!isInteractive ? "is-dim" : ""}`}
                                            onClick={() => isInteractive && setActiveTab(item.id)}
                                        >
                                            <Icon size={15} />
                                            <span>{item.label}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>

                        {/* Content panel */}
                        <div className="lp-product-content">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab}
                                    initial={{ opacity: 0, x: 12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -12 }}
                                    transition={{ duration: 0.22, ease: "easeOut" }}
                                    style={{ height: "100%" }}
                                >
                                    {renderPanel()}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </FadeInSection>

            <FadeInSection delay={0.2}>
                <div className="lp-product-trust-row">
                    <span><Check size={13} /> Actual module names and navigation</span>
                    <span><Check size={13} /> Real data schema, realistic values</span>
                    <span><Check size={13} /> Interactive — click each tab to explore</span>
                </div>
            </FadeInSection>
        </section>
    );
}

/* ===========================================================
   HERO DEMO REQUEST FORM
   =========================================================== */
const jobDesignations = [
    "CEO / Founder",
    "CXO / VP",
    "Director",
    "Manager",
    "HR / Operations",
    "IT / Security",
    "Other",
];

const employeeRanges = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];

const DEMO_OTP_RESEND_COOLDOWN_SECONDS = 60;

function DemoRequestForm() {
    const [form, setForm] = React.useState({
        fullName: "",
        workEmail: "",
        phoneNumber: "",
        companyName: "",
        jobDesignation: "",
        numberOfEmployees: "",
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);

    // "form" -> "otp" -> submitted
    const [stage, setStage] = React.useState("form");
    const [otp, setOtp] = React.useState("");
    const [otpError, setOtpError] = React.useState("");
    const [verifyingOtp, setVerifyingOtp] = React.useState(false);
    const [resendingOtp, setResendingOtp] = React.useState(false);
    const [resendCooldown, setResendCooldown] = React.useState(0);
    const cooldownRef = React.useRef(null);

    React.useEffect(() => {
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, []);

    const startResendCooldown = () => {
        setResendCooldown(DEMO_OTP_RESEND_COOLDOWN_SECONDS);
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        cooldownRef.current = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const response = await apiPublicPost("/api/demo-requests/send-otp", {
                workEmail: form.workEmail,
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                toast.error(data.message || "Something went wrong. Please try again.");
                return;
            }
            setOtp("");
            setOtpError("");
            startResendCooldown();
            setStage("otp");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (verifyingOtp) return;
        setVerifyingOtp(true);
        setOtpError("");
        try {
            const verifyResp = await apiPublicPost("/api/demo-requests/verify-otp", {
                workEmail: form.workEmail,
                otp,
            });
            const verifyData = await verifyResp.json().catch(() => ({}));
            if (!verifyResp.ok) {
                setOtpError(verifyData.message || "Verification failed. Please try again.");
                return;
            }

            const response = await apiPublicPost("/api/demo-requests", form);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                toast.error(data.message || "Something went wrong. Please try again.");
                return;
            }
            trackEvent("Lead", { content_name: "Demo Request" });
            setSubmitted(true);
        } catch {
            setOtpError("Network error. Please check your connection and try again.");
        } finally {
            setVerifyingOtp(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendCooldown > 0 || resendingOtp) return;
        setResendingOtp(true);
        setOtpError("");
        try {
            const response = await apiPublicPost("/api/demo-requests/resend-otp", {
                workEmail: form.workEmail,
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                toast.success("A new verification code has been sent.");
                startResendCooldown();
            } else if (response.status === 429) {
                setOtpError(data.message || "Too many resend requests. Please wait before trying again.");
            } else {
                toast.error(data.message || "Failed to resend code.");
            }
        } catch {
            toast.error("Network error. Please check your connection and try again.");
        } finally {
            setResendingOtp(false);
        }
    };

    if (submitted) {
        return (
            <div className="landing-demo-form-card landing-demo-form-success">
                <div className="landing-demo-form-success-icon">
                    <Check size={28} strokeWidth={2} />
                </div>
                <h3>Demo Request Received</h3>
                <p>
                    Thanks, {form.fullName.split(" ")[0] || "there"}! Our team will
                    reach out shortly to schedule your personalized demo.
                </p>
            </div>
        );
    }

    if (stage === "otp") {
        return (
            <div className="landing-demo-form-card">
                <h3>Verify Your Email</h3>
                <p className="landing-demo-form-lead">
                    We sent a 6-digit code to <strong>{form.workEmail}</strong>. Enter it below to confirm your demo request.
                </p>
                <form onSubmit={handleVerifyOtp} className="landing-demo-form">
                    <div className="landing-demo-form-field">
                        <ShieldCheck size={16} />
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            placeholder="6-digit code"
                            value={otp}
                            onChange={(e) => {
                                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                                setOtpError("");
                            }}
                            autoComplete="one-time-code"
                            required
                            style={{ letterSpacing: "0.3em", textAlign: "center" }}
                        />
                    </div>
                    {otpError && (
                        <p className="landing-demo-otp-error" role="alert">{otpError}</p>
                    )}
                    <button
                        type="submit"
                        className="landing-demo-form-submit"
                        disabled={verifyingOtp || otp.length !== 6}
                    >
                        {verifyingOtp ? "Verifying..." : "Verify & Schedule Demo"}
                    </button>
                </form>
                <div className="landing-demo-otp-resend-row">
                    {resendCooldown > 0 ? (
                        <span className="landing-demo-otp-resend-timer">
                            Resend available in {resendCooldown}s
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="landing-demo-otp-resend-btn"
                            onClick={handleResendOtp}
                            disabled={resendingOtp}
                        >
                            {resendingOtp ? "Sending..." : "Resend Code"}
                        </button>
                    )}
                    <button
                        type="button"
                        className="landing-demo-otp-back-btn"
                        onClick={() => {
                            setStage("form");
                            setOtp("");
                            setOtpError("");
                        }}
                    >
                        Edit details
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="landing-demo-form-card">
            <h3>Book a Personalized Demo</h3>
            <p className="landing-demo-form-lead">See how VisuN AI can transform your business.</p>
            <form onSubmit={handleSubmit} className="landing-demo-form">
                <div className="landing-demo-form-field">
                    <User size={16} />
                    <input
                        type="text"
                        placeholder="Full Name*"
                        value={form.fullName}
                        onChange={handleChange("fullName")}
                        required
                    />
                </div>
                <div className="landing-demo-form-field">
                    <Mail size={16} />
                    <input
                        type="email"
                        placeholder="Work Email*"
                        value={form.workEmail}
                        onChange={handleChange("workEmail")}
                        required
                    />
                </div>
                <div className="landing-demo-form-field">
                    <Phone size={16} />
                    <input
                        type="tel"
                        placeholder="Phone Number*"
                        value={form.phoneNumber}
                        onChange={handleChange("phoneNumber")}
                        required
                    />
                </div>
                <div className="landing-demo-form-field">
                    <Building2 size={16} />
                    <input
                        type="text"
                        placeholder="Company Name*"
                        value={form.companyName}
                        onChange={handleChange("companyName")}
                        required
                    />
                </div>
                <div className="landing-demo-form-field">
                    <Briefcase size={16} />
                    <select value={form.jobDesignation} onChange={handleChange("jobDesignation")} required>
                        <option value="" disabled>Job Designation*</option>
                        {jobDesignations.map((d) => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>
                </div>
                <div className="landing-demo-form-field">
                    <Users size={16} />
                    <select value={form.numberOfEmployees} onChange={handleChange("numberOfEmployees")} required>
                        <option value="" disabled>Number of Employees*</option>
                        {employeeRanges.map((r) => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </div>
                <button type="submit" className="landing-demo-form-submit" disabled={submitting}>
                    {submitting ? "Sending code..." : "Schedule My Live Demo"}
                </button>
            </form>
            <p className="landing-demo-form-note">
                <Lock size={12} /> Your information is secure and will only be used to
                schedule your demo and related communication.
            </p>
        </div>
    );
}

/* ===========================================================
   EXECUTIVE DASHBOARD SHOWCASE (laptop + phone mockup)
   =========================================================== */
const execDashboardBullets = [
    "Live productivity and activity overview",
    "Project progress and task insights",
    "Team performance and utilization",
    "Alerts on risks and productivity gaps",
];

function ExecutiveDashboardShowcase() {
    return (
        <section id="demo" className="landing-exec-dashboard">
            <div className="landing-exec-dashboard-inner">
                <FadeInSection className="landing-exec-dashboard-text">
                    <span className="landing-section-tag">Executive Dashboard</span>
                    <h2 className="landing-exec-dashboard-heading">
                        Your Business at a{" "}
                        <span className="landing-hero-v2-accent">Glance</span>
                    </h2>
                    <p>
                        Real-time insights into productivity, project status, team
                        activity and device performance — everything you need in one
                        powerful dashboard.
                    </p>
                    <ul className="landing-exec-dashboard-list">
                        {execDashboardBullets.map((b) => (
                            <li key={b}><Check size={16} /> {b}</li>
                        ))}
                    </ul>
                </FadeInSection>

                <FadeInSection delay={0.15} className="landing-exec-dashboard-visual">
                    <div className="landing-exec-tablet">
                        <span className="landing-exec-tablet-camera" />
                        <div className="landing-exec-tablet-screen">
                            <video
                                className="landing-exec-tablet-video"
                                controls
                                playsInline
                                preload="metadata"
                                onLoadedMetadata={(e) => { e.currentTarget.currentTime = 1; }}
                            >
                                <source src="/demovideo.mp4" type="video/mp4" />
                            </video>
                        </div>
                    </div>
                </FadeInSection>
            </div>
        </section>
    );
}

/* ===========================================================
   WHY CEOS CHOOSE VISUN AI
   =========================================================== */
const ceoChoiceItems = [
    {
        icon: Users,
        title: "Improve Team Productivity",
        description: "Understand how work gets done and help your team perform at their best.",
    },
    {
        icon: Clock,
        title: "Save Time & Resources",
        description: "Identify time-wasters and remove roadblocks that slow down progress.",
    },
    {
        icon: TrendingUp,
        title: "Data-Driven Decisions",
        description: "Use accurate insights to make confident decisions that drive results.",
    },
    {
        icon: Shield,
        title: "Reduce Business Risks",
        description: "Stay informed about insider risks, data leaks and policy violations.",
    },
    {
        icon: Trophy,
        title: "Achieve Business Goals",
        description: "Align teams, track progress and achieve goals faster with complete visibility.",
    },
];

function WhyCeosChoose() {
    return (
        <section className="landing-ceo-choice">
            <FadeInSection>
                <div className="landing-section-header">
                    <span className="landing-section-tag">Why CEOs Choose VisuN AI</span>
                    <h2 className="landing-ceo-choice-heading">Make Every Minute, Count</h2>
                </div>
            </FadeInSection>
            <div className="landing-ceo-choice-grid">
                {ceoChoiceItems.map((item, i) => {
                    const Icon = item.icon;
                    return (
                        <FadeInSection key={item.title} delay={i * 0.08} className="landing-ceo-choice-card">
                            <div className="landing-ceo-choice-icon">
                                <Icon size={26} strokeWidth={1.6} />
                            </div>
                            <h3>{item.title}</h3>
                            <p>{item.description}</p>
                        </FadeInSection>
                    );
                })}
            </div>
        </section>
    );
}

/* ===========================================================
   HOW IT WORKS SECTION
   =========================================================== */
function HowItWorks() {
    return (
        <section className="landing-how-it-works">
            <FadeInSection>
                <div className="landing-section-header">
                    <span className="landing-section-tag">How It Works</span>
                    <h2>
                        Up and running in{" "}
                        <span className="text-accent">under 10 minutes</span>
                    </h2>
                    <p>
                        No complex infrastructure. No professional services engagement.
                        Deploy agents and start monitoring in minutes.
                    </p>
                </div>
            </FadeInSection>

            <div className="landing-hiw-steps">
                {howItWorksSteps.map((step, i) => {
                    const Icon = step.icon;
                    return (
                        <FadeInSection key={step.step} delay={i * 0.12}>
                            <div className="landing-hiw-step">
                                <div className="landing-hiw-step-number">{step.step}</div>
                                <div className="landing-hiw-icon">
                                    <Icon size={26} strokeWidth={1.5} />
                                </div>
                                <h3>{step.title}</h3>
                                <p>{step.description}</p>
                                {i < howItWorksSteps.length - 1 && (
                                    <div className="landing-hiw-connector" />
                                )}
                            </div>
                        </FadeInSection>
                    );
                })}
            </div>
        </section>
    );
}

/* ===========================================================
   COMPETITOR COMPARISON
   =========================================================== */
const comparePlatforms = [
    "VisuN AI",
    "ActivTrak",
    "Hubstaff",
    "Time Doctor",
    "Insightful",
    "Teramind",
    "Veriato",
    "Monitask",
];

// One entry per row; `values` follows the order of comparePlatforms.
const compareRows = [
    { label: "Primary Business Focus", justify: true, values: ["Employee monitoring + workforce intelligence", "Workforce intelligence", "Time + workforce operations", "Productivity + time management", "Work intelligence", "Monitoring + insider risk", "Insider risk + behavioral security", "Affordable employee monitoring"] },
    { label: "Employee Activity", values: ["Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes"] },
    { label: "Screenshots", values: ["Yes", "Partial", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes"] },
    { label: "Apps & Websites", values: ["Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes"] },
    { label: "Time & Attendance", values: ["Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes"] },
    { label: "Productivity Analytics", values: ["Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes"] },
    { label: "Security / Risk", values: ["Yes", "Partial", "Partial", "Partial", "Partial", "Yes", "Yes", "Partial"] },
    { label: "IDP / SSO", values: ["Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "No"] },
    { label: "Salary & Data Leakage", values: ["Yes", "No", "No", "No", "Yes", "Yes", "Yes", "No"] },
    { label: "USB Blocking", values: ["Yes", "Yes", "No", "No", "No", "Yes", "Yes", "No"] },
    { label: "AI Chatbot / Conversational AI", values: ["Yes", "Yes", "Partial", "Yes", "Yes", "No", "No", "No"] },
    { label: "Screen Wall", values: ["Yes", "No", "No", "No", "No", "Yes", "No", "No"] },
];

// The last N rows are where VisuN AI is called out.
const COMPARE_HIGHLIGHT_ROWS = 6;

function CompareCell({ value }) {
    const text = String(value).replace(/^\(Partial\)\s*/i, "Partial — ");
    if (/^yes\b/i.test(text)) {
        const detail = text.replace(/^yes,?\s*/i, "");
        return (
            <span className="landing-compare-val is-yes">
                <Check size={15} strokeWidth={2.5} />
                <span>{detail || "Yes"}</span>
            </span>
        );
    }
    if (/^no\b/i.test(text)) {
        return (
            <span className="landing-compare-val is-no">
                <X size={15} strokeWidth={2.5} />
                <span>{text.length > 2 ? text : "No"}</span>
            </span>
        );
    }
    if (/^partial/i.test(text)) {
        return (
            <span className="landing-compare-val is-partial">
                <Minus size={15} strokeWidth={2.5} />
                <span>{text}</span>
            </span>
        );
    }
    return <span className="landing-compare-val is-text">{text}</span>;
}

function ComparisonSection() {
    const firstHighlight = compareRows.length - COMPARE_HIGHLIGHT_ROWS;

    // The racing-circuit ring is one overlay spanning all highlighted VisuN AI
    // cells, so it is positioned from the measured first/last highlighted cell
    // (re-measured on resize / zoom / font load).
    const tableRef = React.useRef(null);
    const firstCellRef = React.useRef(null);
    const lastCellRef = React.useRef(null);
    const [ringRect, setRingRect] = React.useState(null);

    React.useLayoutEffect(() => {
        const table = tableRef.current;
        if (!table) return undefined;
        const measure = () => {
            const first = firstCellRef.current;
            const last = lastCellRef.current;
            if (!first || !last) return;
            setRingRect({
                left: first.offsetLeft,
                top: first.offsetTop,
                width: first.offsetWidth,
                height: last.offsetTop + last.offsetHeight - first.offsetTop,
            });
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(table);
        document.fonts?.ready?.then(measure);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="compare" className="landing-compare">
            <FadeInSection>
                <div className="landing-section-header">
                    <span className="landing-section-tag">Comparison</span>
                    <h2>
                        VisuN AI vs the <span className="text-accent">top employee monitoring platforms</span>
                    </h2>
                    <p>
                        More capability per dollar — from security and data-leakage
                        protection to payroll and conversational AI, all at a fraction
                        of the price.
                    </p>
                </div>
            </FadeInSection>

            <FadeInSection delay={0.1}>
                <div className="landing-compare-scroll">
                    <div className="landing-compare-inner">
                        <table className="landing-compare-table" ref={tableRef}>
                            <thead>
                                <tr>
                                    <th className="landing-compare-corner" scope="col">Platform</th>
                                    {comparePlatforms.map((name, c) => (
                                        <th
                                            key={name}
                                            scope="col"
                                            className={c === 0 ? "is-visun is-visun-head" : ""}
                                        >
                                            {name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {compareRows.map((row, r) => {
                                    const isHighlight = r >= firstHighlight;
                                    const isLast = r === compareRows.length - 1;
                                    return (
                                        <tr
                                            key={row.label}
                                            className={[
                                                isHighlight ? "is-highlight-row" : "",
                                                row.justify ? "is-justify-row" : "",
                                            ].join(" ").trim()}
                                        >
                                            <th scope="row" className="landing-compare-rowhead">{row.label}</th>
                                            {row.values.map((value, c) => {
                                                const classes = [];
                                                if (c === 0) {
                                                    classes.push("is-visun");
                                                    if (isHighlight) classes.push("is-visun-hl");
                                                    if (r === firstHighlight) classes.push("is-hl-first");
                                                    if (isLast) classes.push("is-hl-last");
                                                }
                                                return (
                                                    <td
                                                        key={comparePlatforms[c]}
                                                        className={classes.join(" ")}
                                                        ref={c === 0 && r === firstHighlight ? firstCellRef : c === 0 && isLast ? lastCellRef : undefined}
                                                    >
                                                        <CompareCell value={value} />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {ringRect && (
                            <>
                                <div className="landing-compare-glow" aria-hidden="true" style={ringRect} />
                                <div className="landing-compare-ring" aria-hidden="true" style={ringRect} />
                            </>
                        )}
                    </div>
                </div>
                <p className="landing-compare-note">
                    Prices shown are public entry-level rates, generally billed annually.
                </p>
            </FadeInSection>
        </section>
    );
}

/* ===========================================================
   MAIN LANDING PAGE
   =========================================================== */
export default function LandingPage() {
    const navigate = useNavigate();
    const isLoggedIn = !!getToken();
    const [pricingRows, setPricingRows] = React.useState(() => mergeLandingTiersWithServer([]));
    const [systemConfig, setSystemConfig] = React.useState({
        announcementText: "",
        announcementEnabled: false,
    });

    // Float nav background on scroll
    const [scrolled, setScrolled] = React.useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchPlans = async () => {
            try {
                const response = await apiPublicGet("/api/subscriptions/plans");
                if (!response.ok) return;
                const payload = await response.json().catch(() => ({}));
                if (!isMounted) return;
                setPricingRows(mergeLandingTiersWithServer(payload?.plans));
            } catch {
                if (!isMounted) return;
                setPricingRows(mergeLandingTiersWithServer([]));
            }
        };

        void fetchPlans();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const fetchSystemConfig = async () => {
            try {
                const response = await apiPublicGet("/api/system-config/public");
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && isMounted) {
                        setSystemConfig({
                            announcementText: data.announcementText || "",
                            announcementEnabled: !!data.announcementEnabled,
                        });
                    }
                }
            } catch (err) {
                console.error("Failed to load landing page announcement", err);
            }
        };
        void fetchSystemConfig();
        return () => {
            isMounted = false;
        };
    }, []);

    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    // Measures the hero CTA button row so the carousel's dot/arrow nav bar
    // can be sized to match it exactly (--cta-row-width, read by
    // .landing-hero-main-controls in landing.css) — keeps the "next" arrow
    // aligned with the right edge of the Start 7-Day Free Trial button
    // instead of drifting to the far edge of the column, and stays correct
    // across resizes since button label width is responsive.
    const heroCtaRowRef = React.useRef(null);
    const [heroCtaRowWidth, setHeroCtaRowWidth] = React.useState(0);

    React.useEffect(() => {
        const el = heroCtaRowRef.current;
        if (!el) return;
        const measure = () => setHeroCtaRowWidth(el.offsetWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);
    const closeMobileMenu = () => setMobileMenuOpen(false);

    const openSubscriptionModal = React.useCallback((tier) => {
        if (!tier) return;
        if (!isLoggedIn) {
            toast.info("Please login to manage subscriptions.");
            navigate(`/login?returnTo=${encodeURIComponent("/billing")}`);
            return;
        }
        navigate("/billing");
    }, [isLoggedIn, navigate]);

    return (
        <div className="landing-page">
            {systemConfig.announcementEnabled && systemConfig.announcementText && (
                <div className="landing-marquee-banner">
                    <div className="landing-marquee-track">
                        <span>{systemConfig.announcementText}</span>
                        <span aria-hidden="true">{systemConfig.announcementText}</span>
                    </div>
                </div>
            )}
            {/* --- Top Navbar --- */}
            <nav className={`landing-nav ${scrolled ? "landing-nav--scrolled" : ""}`} style={systemConfig.announcementEnabled && systemConfig.announcementText ? { top: "36px" } : {}}>
                <div className="landing-nav-inner">
                    <Link to="/" className="landing-nav-brand">
                        <img src={logo} alt="VisuN AI by Global Cyber Associates" className="landing-nav-logo" />
                        <span className="landing-nav-wordmark">VisuN AI</span>
                    </Link>

                    <div className={`landing-nav-links ${mobileMenuOpen ? "is-open" : ""}`}>
                        <a href="#demo" onClick={closeMobileMenu}>Watch Demo</a>
                        <a href="#product" onClick={closeMobileMenu}>Product</a>
                        <a href="#features" onClick={closeMobileMenu}>Features</a>
                        <a href="#compare" onClick={closeMobileMenu}>Compare</a>
                        <a href="#pricing" onClick={closeMobileMenu}>Pricing</a>
                        <a href="#about" onClick={closeMobileMenu}>About</a>
                        <a href="#contact" onClick={closeMobileMenu}>Contact</a>
                        <div className="landing-nav-divider" />
                        <Link to="/login" className="landing-nav-login" onClick={closeMobileMenu}>
                            Login
                        </Link>
                        <Link to="/register" className="landing-nav-cta" onClick={closeMobileMenu}>
                            Get Started <ArrowRight size={16} />
                        </Link>
                    </div>

                    <div className="landing-nav-right">
                        <ThemeToggle />
                        <button
                            className="landing-nav-toggle"
                            onClick={toggleMobileMenu}
                            aria-label="Toggle Menu"
                        >
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Overlay Backdrop */}
                {mobileMenuOpen && (
                    <div className="landing-nav-overlay" onClick={closeMobileMenu} />
                )}
            </nav>

            {/* --- Hero --- */}
            <section
                className="landing-hero-v2"
                style={systemConfig.announcementEnabled && systemConfig.announcementText ? { paddingTop: "176px" } : {}}
            >
                <div className="landing-hero-v2-inner">
                    <motion.div
                        className="landing-hero-v2-left"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        style={heroCtaRowWidth ? { "--cta-row-width": `${heroCtaRowWidth}px` } : undefined}
                    >
                        <HeroMainCarousel />
                        <div className="landing-hero-v2-btns" ref={heroCtaRowRef}>
                            <button
                                className="landing-hero-v2-btn-primary"
                                onClick={() => {
                                    document.getElementById("demo-form")?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "center",
                                    });
                                }}
                            >
                                Book a Live Demo
                            </button>
                            <button
                                className="landing-hero-v2-btn-outline"
                                onClick={() => navigate("/register")}
                            >
                                Start 7-Day Free Trial
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        id="demo-form"
                        className="landing-hero-v2-right"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
                    >
                        <DemoRequestForm />
                    </motion.div>
                </div>
            </section>

            {/* --- Hero Value Strip / Why VisuN AI --- */}
            <section className="landing-value-strip">
                <FadeInSection className="landing-value-strip-header">
                    <h2>Why VisuN AI</h2>
                </FadeInSection>
                <div className="landing-value-strip-grid">
                    {heroValueStripItems.map((item, i) => {
                        const Icon = item.icon;
                        return (
                            <FadeInSection key={item.title} delay={i * 0.08} className="landing-value-strip-item">
                                <div className="landing-value-strip-icon">
                                    <Icon size={22} strokeWidth={1.75} />
                                </div>
                                <div>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </div>
                            </FadeInSection>
                        );
                    })}
                </div>
            </section>

            {/* --- Executive Dashboard Showcase --- */}
            <ExecutiveDashboardShowcase />

            {/* --- Why CEOs Choose VisuN AI --- */}
            <WhyCeosChoose />

            {/* --- Product Preview (linear.com style) --- */}
            <ProductPreview />

            {/* --- How It Works --- */}
            <HowItWorks />

            {/* --- Features --- */}
            <section id="features" className="landing-features">
                <FadeInSection>
                    <div className="landing-section-header">
                        <span className="landing-section-tag">Features</span>
                        <h2>
                            Everything you need to <span className="text-accent">monitor employees & secure endpoints</span>
                        </h2>
                        <p>
                            From real-time employee monitoring to executive analytics — VisuN AI
                            replaces an entire endpoint security and workforce productivity
                            stack with a single, beautiful platform.
                        </p>
                    </div>
                </FadeInSection>

                <div className="landing-features-grid">
                    {features.map((f, i) => (
                        <FeatureCard
                            key={f.title}
                            icon={f.icon}
                            title={f.title}
                            description={f.description}
                            delay={i * 0.08}
                        />
                    ))}
                </div>
            </section>

            {/* --- Competitor Comparison --- */}
            <ComparisonSection />

            {/* Pricing */}
            <section id="pricing" className="landing-pricing">
                <FadeInSection>
                    <div className="landing-section-header">
                        <span className="landing-section-tag">Pricing</span>
                        <h2>
                            Flexible plans for <span className="text-accent">every team size</span>
                        </h2>
                        <p>
                            Start small, scale fast, and upgrade as your security
                            operations mature.
                        </p>
                    </div>
                </FadeInSection>

                <div className="landing-pricing-grid">
                    {pricingRows.map((tier, i) => {
                        const isNumericPrice = tier.price !== "Free" && !tier.contactSales && /^\d/.test(tier.price);
                        const seatLabel = tier.maxSystems
                            ? `Up to ${tier.maxSystems} agents`
                            : tier.seatLimit
                                ? `Up to ${tier.seatLimit} agents`
                                : "Unlimited agents";
                        return (
                            <FadeInSection
                                key={tier.id || tier.name}
                                delay={i * 0.1}
                                className="landing-pricing-item"
                            >
                                <article
                                    className={`landing-pricing-card ${tier.featured ? "landing-pricing-card--featured" : ""}`}
                                >
                                    {tier.featured && (
                                        <span className="landing-pricing-pill">Most Popular</span>
                                    )}
                                    <h3>{tier.name}</h3>
                                    <p className="landing-pricing-description">{tier.description}</p>
                                    <div className="landing-pricing-price">
                                        {isNumericPrice && (
                                            <span className="landing-pricing-currency">₹</span>
                                        )}
                                        <span>{tier.price}</span>
                                        <small>{tier.billing}</small>
                                    </div>
                                    <div className="landing-pricing-seat">
                                        <Users size={13} />
                                        <span>{seatLabel}</span>
                                    </div>
                                    <ul className="landing-pricing-features">
                                        {tier.features.map((item) => (
                                            <li key={item}>
                                                <Check size={15} />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                        {Array.isArray(tier.unavailableFeatures) && tier.unavailableFeatures.map((item) => (
                                            <li key={`na-${item}`} className="is-unavailable">
                                                <Minus size={15} />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        className={tier.featured ? "landing-btn-primary landing-pricing-btn" : "landing-btn-secondary landing-pricing-btn"}
                                        onClick={() => openSubscriptionModal(tier)}
                                    >
                                        {isLoggedIn ? "Open Billing" : tier.cta}
                                    </button>
                                </article>
                            </FadeInSection>
                        );
                    })}
                </div>
            </section>

            {/* --- About / Mission --- */}
            <section id="about" className="landing-about">
                <FadeInSection>
                    <div className="landing-about-inner">
                        <div className="landing-about-text">
                            <span className="landing-section-tag">About Us</span>
                            <h2>Innovation, Integrity &amp; Impact</h2>
                            <p>
                                Global Cyber Associates (GCA) is a cybersecurity company
                                dedicated to building intelligent, AI-powered solutions that give
                                organizations full visibility and control over their digital
                                infrastructure.
                            </p>
                            <p>
                                Our flagship product <strong>VisuN AI</strong> combines endpoint
                                monitoring, network topology visualization, vulnerability
                                management, and workforce analytics into a single unified
                                platform - purpose-built for IT teams, CISOs, and
                                C-suite executives.
                            </p>
                            <blockquote>
                                "It's not just our mission, it's our culture."
                            </blockquote>
                        </div>
                        <div className="landing-about-visual">
                            <div className="landing-about-card">
                                <Eye size={40} strokeWidth={1.2} />
                                <span>Complete Visibility</span>
                            </div>
                            <div className="landing-about-card">
                                <Shield size={40} strokeWidth={1.2} />
                                <span>Enterprise Security</span>
                            </div>
                            <div className="landing-about-card">
                                <Brain size={40} strokeWidth={1.2} />
                                <span>AI-Powered Insights</span>
                            </div>
                        </div>
                    </div>
                </FadeInSection>
            </section>

            {/* --- Contact / Branches --- */}
            <section id="contact" className="landing-contact">
                <FadeInSection>
                    <div className="landing-section-header">
                        <span className="landing-section-tag">Contact</span>
                        <h2>Let's Build the Future Together</h2>
                        <p>
                            Reach out to any of our global offices - we'd love to hear from
                            you.
                        </p>
                    </div>
                </FadeInSection>

                <div className="landing-branches">
                    {branchLocations.map((b, i) => {
                        const fullAddress = `${b.addressLine1}${b.addressLine2 ? `, ${b.addressLine2}` : ""}, ${b.city}, ${b.state}, ${b.country}`;
                        const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
                        return (
                            <FadeInSection key={i} delay={i * 0.12}>
                                <div className="landing-branch-card">
                                    <div className="landing-branch-header">
                                        <span className="landing-branch-flag">{b.countryCode}</span>
                                        <div>
                                            <h3>{b.title}</h3>
                                            <p className="landing-branch-subtitle">{b.subtitle}</p>
                                        </div>
                                    </div>
                                    <div className="landing-branch-map">
                                        <iframe
                                            title={`Map-${i}`}
                                            src={`https://www.google.com/maps?q=${encodeURIComponent(
                                                fullAddress
                                            )}&output=embed`}
                                            allowFullScreen
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="landing-branch-info">
                                        <MapPin size={16} />
                                        <span>
                                            {b.addressLine1}
                                            {b.addressLine2 ? `, ${b.addressLine2}` : ""}
                                            <br />
                                            {b.city}, {b.state} {b.zip}
                                            <br />
                                            {b.country}
                                        </span>
                                    </div>
                                    <a
                                        className="landing-branch-directions"
                                        href={directionsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Get Directions <ArrowRight size={14} />
                                    </a>
                                </div>
                            </FadeInSection>
                        );
                    })}
                </div>
            </section>

            {/* --- Final CTA Banner --- */}
            <section className="landing-trusted-cta">
                <FadeInSection delay={0.1}>
                    <div className="landing-trusted-cta-card">
                        <div className="landing-trusted-cta-icon">
                            <CalendarClock size={22} />
                        </div>
                        <div className="landing-trusted-cta-text">
                            <h3>
                                Ready to <span className="landing-hero-highlight">Transform Your Business?</span>
                            </h3>
                            <p>
                                See your team's real productivity score in under 10 minutes.
                                Book a personalized demo and discover how VisuN AI can
                                help you improve productivity, ensure accountability and
                                drive growth. No surveillance. Just results.
                            </p>
                            <div
                                className="landing-pricing-trust-pills"
                                style={{ justifyContent: "flex-start", marginTop: "16px", marginBottom: 0 }}
                            >
                                <span><Check size={13} /> No credit card</span>
                                <span><Check size={13} /> 7-day full access</span>
                                <span><Check size={13} /> Setup &lt; 10 min</span>
                                <span><Check size={13} /> Cancel anytime</span>
                            </div>
                        </div>
                        <div className="landing-trusted-cta-actions">
                            <button
                                className="landing-hero-v2-btn-primary"
                                onClick={() => {
                                    document.getElementById("demo-form")?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "center",
                                    });
                                }}
                            >
                                Book a Live Demo
                            </button>
                            <button
                                className="landing-trusted-cta-link"
                                onClick={() => navigate("/register")}
                            >
                                Start 7-Day Free Trial <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>
                </FadeInSection>
            </section>

            {/* --- Footer Trust Bar --- */}
            <section className="landing-footer-trust-bar">
                <span><CalendarCheck size={16} /> 7-Day Free Trial</span>
                <span><CreditCard size={16} /> No Credit Card Required</span>
                <span><Zap size={16} /> Quick &amp; Easy Setup</span>
                <span><ShieldCheck size={16} /> Enterprise-Grade Security</span>
                <span><Headphones size={16} /> 24/7 Customer Support</span>
            </section>

            {/* --- Footer --- */}
            <footer className="landing-footer">
                <div className="landing-footer-inner">
                    <div className="landing-footer-brand">
                        <div className="landing-footer-brand-logo">
                            <img src={logo} alt="VisuN AI by Global Cyber Associates" />
                            <span>VisuN AI</span>
                        </div>
                        <p className="landing-footer-company">Global Cyber Associates</p>
                        <p className="landing-footer-tagline">AI-Powered Endpoint Intelligence Platform for<br />Modern Enterprise Security</p>
                        <div className="landing-footer-social">
                            <a href={`mailto:${SUPPORT_EMAIL}`} title="Email">
                                <Mail size={18} />
                            </a>
                            <a href="#contact" title="Locations">
                                <MapPin size={18} />
                            </a>
                            <a href="#about" title="About">
                                <Globe size={18} />
                            </a>
                        </div>
                    </div>

                    <div className="landing-footer-links">
                        <div>
                            <h4>Product</h4>
                            <a href="#features">Features</a>
                            <a href="#pricing">Pricing</a>
                            <Link to="/login">Dashboard</Link>
                            <Link to="/login">Download Agent</Link>
                        </div>
                        <div>
                            <h4>Company</h4>
                            <a href="#about">About Us</a>
                            <a href="#contact">Contact</a>
                            <a href={`mailto:${SUPPORT_EMAIL}`}>Support</a>
                        </div>
                        <div>
                            <h4>Legal</h4>
                            <a href="/pages/privacy">Privacy Policy</a>
                            <a href="/pages/terms">Terms of Service</a>
                        </div>
                    </div>
                </div>

                <div className="landing-footer-bottom">
                    <p>© {new Date().getFullYear()} Global Cyber Associates. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}
